import { FC, useState, useEffect, useRef } from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import { PromptCategorySummaryResult } from "@app/operations/ai-response-review/interfaces";

const AiResponseSummaryFlyout: FC = () => {
  const [result, setResult] = useState<PromptCategorySummaryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useRef(new AiResponseReviewService()).current;

  useEffect(() => {
    const loadMostRecent = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await service.getMostRecentPromptCategorySummary();
        setResult(data);
      } catch (err) {
        setError("Failed to load prompt category summary.");
      } finally {
        setLoading(false);
      }
    };

    loadMostRecent();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);

    try {
      const data = await service.generatePromptCategorySummary();
      setResult(data);
    } catch (err) {
      setError("Failed to generate a new prompt category summary.");
      console.error("Error generating prompt category summary:", err);
    } finally {
      setGenerating(false);
    }
  };

  const formatGeneratedAt = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });

  return (
    <div className="ai-response-review__summary p-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        {result && (
          <p className="text-muted m-0">
            Generated: {formatGeneratedAt(result.generatedAt)} &mdash; {result.rowCount.toLocaleString()} response{result.rowCount !== 1 ? "s" : ""} analyzed
          </p>
        )}
        {!result && !loading && (
          <p className="text-muted">No summary generated yet.</p>
        )}
        <span className="d-flex align-items-center ms-auto">
          <strong className="m-0 me-2 ai-response-review__warning">
            Generating a summary uses AI tokens and may incur high cost.
          </strong>
          <QESButton
            qesTestId="ai-response-review__generate-summary-button"
            qesType="primary"
            onClick={handleGenerate}
            disabled={generating || loading}
          >
            {generating ? "Generating…" : "Generate New Summary"}
          </QESButton>
        </span>
      </div>

      {(loading || generating) && (
        <div className="mt-3">
          <LoadingSpinner />
        </div>
      )}

      {error && !loading && !generating && (
        <QESAlert type="danger" persist={true}>{error}</QESAlert>
      )}

      {result && !loading && !generating && (
        <Markdown remarkPlugins={[remarkGfm]}>
          {result.summary}
        </Markdown>
      )}
    </div>
  );
};

export default AiResponseSummaryFlyout;

